import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { brandConfig } from '../../config/brands';
import { manualProductTabLabel } from '../../config/brands/productLabels';
import { useProducts } from '../../features/products/hooks';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { apiClient } from '../../services/apiClient';
import { formatDateTime as formatDate } from '../../utils/formatters';
import type { ProductListParams } from '../../features/products/types';
import { renderMaterialPill } from './shared/MaterialCountPill';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import { useBrandFilterOptions } from '../../features/products/useBrandFilterOptions';
import { ProductEntryModal } from './shared/ProductEntryModal';
import { CreateButton } from '../../components/common/CreateButton';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import './ProductsPageHeader.css';
import './ManualProductListPage.css';

const SORT_STORAGE_KEY = 'manualProducts_sortBy';
const BRAND_FILTER_STORAGE_KEY = 'manualProducts_brand_filter';

const PRODUCTS_TAB_KEY = 'products_last_tab';

export default function ManualProductListPage() {
  useEffect(() => {
    localStorage.setItem(PRODUCTS_TAB_KEY, '/products/manual');
  }, []);
  const navigate = useNavigate();
  const openWithProductFilter = useMaterialsPanelStore((s) => s.openWithProductFilter);

  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem(SORT_STORAGE_KEY) || 'created_at_desc';
  });
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortBy);
  }, [sortBy]);

  const parsedSort = sortBy.match(/^(.+)_(asc|desc)$/);
  const sortField = parsedSort ? parsedSort[1] : 'created_at';
  const sortOrder = parsedSort ? parsedSort[2] : 'desc';

  const handleSort = (field: string, defaultOrder: 'asc' | 'desc' = 'asc') => {
    if (sortField === field) {
      setSortBy(`${field}_${sortOrder === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSortBy(`${field}_${defaultOrder}`);
    }
  };

  const sortIndicator = (field: string) => {
    if (sortField !== field) return null;
    return <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  // URL の brand_id があればそれを優先、無ければ LocalStorage から復元
  const [searchParams] = useSearchParams();
  const [brandFilter, setBrandFilter] = useState(() => {
    const fromUrl = searchParams.get('brand_id');
    if (fromUrl) return fromUrl;
    return localStorage.getItem(BRAND_FILTER_STORAGE_KEY) || '';
  });

  // URL の brand_id が後から変わったら追従
  useEffect(() => {
    const fromUrl = searchParams.get('brand_id');
    if (fromUrl && fromUrl !== brandFilter) {
      setBrandFilter(fromUrl);
    }
  }, [searchParams]);

  // brandFilter が変更されたら LocalStorage に保存
  useEffect(() => {
    localStorage.setItem(BRAND_FILTER_STORAGE_KEY, brandFilter);
  }, [brandFilter]);

  const params: ProductListParams = {
    limit: 50,
    sort: sortField,
    order: sortOrder as 'asc' | 'desc',
    ...(brandFilter ? { brand_id: brandFilter } : {}),
  };

  const { data, isLoading } = useProducts(params);

  // ブランド一覧（マニュアル商品が存在するブランドのみ）
  const { data: brandsData } = useQuery({
    queryKey: ['brands', 'manual'],
    queryFn: async () => {
      const response = await apiClient.get('/brands', { params: { source: 'manual' } });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const brands: { id: string; brand_name: string }[] = brandsData?.brands ?? [];

  // ブランドフィルタ選択肢 (お気に入りを上位＋星アイコン)
  const brandOptions = useBrandFilterOptions('manual');

  // 新規登録は登録方法の 2 択をモーダルで出し、選んだら登録ページ (/products/manual/new) へ。案件行は「登録する」で初めて作る (入口フロー文書 §6.7)。
  // ブランドで絞り込んでいるときはそのブランドを選択済みにして 2 択を飛ばす (案C)
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const handleCreateNew = () => {
    if (brandFilter) {
      navigate(`/products/manual/new?brand_id=${encodeURIComponent(brandFilter)}`);
      return;
    }
    setEntryModalOpen(true);
  };

  const products = data?.products || [];
  const total = data?.total || 0;
  const quota = data?.quota;
  // プラン上限あり (max が数値) かつ未到達のみ新規登録可。max が null は無制限。
  const canCreate = quota?.can_create ?? true;

  return (
    <div className="manual-product-list-page">
      <ProductEntryModal isOpen={entryModalOpen} onClose={() => setEntryModalOpen(false)} />
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/database_2.svg" alt="" className="page-title-icon page-title-icon--mobile-hidden" />
            {brandConfig.labels.productShort}一覧
          </h1>
        </div>
      </header>

      {/* タブが複数あるときのみタブバー (とディスクリプション) を表示。
          Amazon 連携が無効なパートナー (SAITE 等) はタブが 1 つだけなので非表示。 */}
      {brandConfig.features.amazonIntegration && (
        <nav className="products-page-tabs-bar">
          <div className="products-page-tabs">
            {(() => {
              const amazonTab = (
                <Link key="amazon" to="/products/amazon" className="products-tab">
                  <img src="/amazon_icon.svg" alt="" className="products-tab-icon" />
                  <span>Amazon連携</span>
                </Link>
              );
              const manualTab = (
                <Link key="manual" to="/products/manual" className="products-tab active">
                  <img src="/e_commerce_touch_buy.svg" alt="" className="products-tab-icon" />
                  <span>{manualProductTabLabel(brandConfig)}</span>
                </Link>
              );
              return brandConfig.features.productsManualFirst
                ? [manualTab, amazonTab]
                : [amazonTab, manualTab];
            })()}
          </div>
          <p className="page-description products-tab-description">手動で{brandConfig.labels.productShort}データを登録します</p>
        </nav>
      )}

      <section className="fetch-section">
        <div className="fetch-mode-buttons">
          <CreateButton
            onClick={handleCreateNew}
            disabled={!canCreate}
            title={!canCreate ? 'プランの商品数上限に達しています' : undefined}
          >
            {`1${brandConfig.labels.productShort}ごと登録`}
          </CreateButton>
          <button
            type="button"
            className="btn-modal"
            onClick={() => navigate('/products/manual/upload')}
            disabled={!canCreate}
            title={!canCreate ? 'プランの商品数上限に達しています' : undefined}
          >
            <img src="/export_file.svg" alt="" style={{ width: '14px', height: '14px' }} />
            Excel・CSVから一括登録
          </button>
          {quota && quota.max != null && (
            <div className="product-quota-block">
              <p className="product-quota-note">
                {brandConfig.labels.productShort}数 {quota.used} / {quota.max}
              </p>
              {!canCreate && (
                <p className="product-quota-warning">
                  上限に達しています
                  <Link to="/subscription" className="product-quota-warning-link">プランを確認</Link>
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="list-controls">
        {/* スマホ用トグルボタン */}
        <button
          type="button"
          className="filter-toggle-button"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <img src="/filter_sort_lines_descending.svg" alt="" className="filter-toggle-icon" />
          <span>フィルタ・並び替え</span>
          <span className="filter-toggle-count">全 {total} 件</span>
          <span className={`filter-toggle-chevron ${isFilterExpanded ? 'expanded' : ''}`} />
        </button>

        <div className={`controls-row ${isFilterExpanded ? 'expanded' : ''}`}>
          <div className="filter-section">
            <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
            <span className="filter-label">フィルタ</span>
            <div className="filter-group">
              <label>ブランド</label>
              <SingleSelectDropdown
                label="すべて"
                labelOnly
                value={brandFilter}
                onChange={setBrandFilter}
                options={brandOptions}
                fitContent
              />
            </div>
            <div className="total-count">
              全 <strong>{total}</strong> 件
            </div>
          </div>
          <div className="sort-section" />
        </div>
      </section>

      {isLoading ? (
        <div className="loading-container">
          <p>読み込み中...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <p>商品データがありません</p>
          <p>「フォームから追加」または「CSV/Excelから追加」で商品を登録してください。</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="manual-products-table common-table">
            <thead>
              <tr>
                <th className="col-image">画像</th>
                {brandConfig.features.skuFields && (
                  <th className={`desktop-only sortable-th${sortField === 'sku' ? ' active-sort' : ''}`} onClick={() => handleSort('sku', 'asc')}>SKU{sortIndicator('sku')}</th>
                )}
                <th className={`col-title desktop-only sortable-th${sortField === 'title' ? ' active-sort' : ''}`} onClick={() => handleSort('title', 'asc')}>{brandConfig.labels.productName}{sortIndicator('title')}</th>
                <th className="desktop-only">ブランド</th>
                <th className="col-count desktop-only">{brandConfig.labels.productShort}画像数</th>
                <th className="col-count desktop-only">共有素材</th>
                <th className="col-count desktop-only">個人素材</th>
                <th className={`desktop-only sortable-th${sortField === 'updated_at' ? ' active-sort' : ''}`} onClick={() => handleSort('updated_at', 'desc')}>最終更新日時{sortIndicator('updated_at')}</th>
                <th className="col-mobile-info mobile-only">{brandConfig.labels.productShort}情報</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="clickable-row"
                  onClick={() => navigate(`/products/manual/${product.id}`)}
                >
                  <td className="col-image">
                    {product.main_image_url && product.main_image_url.trim() ? (
                      <img
                        src={product.main_image_url}
                        alt={product.title}
                        className="product-thumbnail"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          const placeholder = document.createElement('div');
                          placeholder.className = 'product-thumbnail-placeholder';
                          placeholder.textContent = 'No Image';
                          target.parentElement?.replaceChild(placeholder, target);
                        }}
                      />
                    ) : (
                      <div className="product-thumbnail-placeholder">No Image</div>
                    )}
                  </td>
                  {brandConfig.features.skuFields && (
                    <td className="desktop-only">{product.sku || '-'}</td>
                  )}
                  <td className="col-title desktop-only">{product.title}</td>
                  <td className="desktop-only">
                    <div className="brand-cell">
                      <span className="brand-english-name">
                        {product.brand_name || '-'}
                      </span>
                      {product.brand_id && (
                        <Link to={`/brands/${product.brand_id}`} className="brand-link" onClick={(e) => e.stopPropagation()}>
                          {brands.find(b => b.id === product.brand_id)?.brand_name || product.brand_name}
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="col-count desktop-only">
                    <span className="image-count-value">{product.image_count ?? 0}</span>
                  </td>
                  <td className="col-count desktop-only">
                    {renderMaterialPill('shared', product.cloud_material_count ?? 0, () => openWithProductFilter(`manual:${product.id}`, 'shared'))}
                  </td>
                  <td className="col-count desktop-only">
                    {renderMaterialPill('personal', product.temp_material_count ?? 0, () => openWithProductFilter(`manual:${product.id}`, 'personal'))}
                  </td>
                  <td className="desktop-only">{formatDate(product.updated_at || product.created_at)}</td>
                  {/* モバイル用：情報を縦積みで表示 */}
                  <td className="col-mobile-info mobile-only">
                    <div className="mobile-info-row1">
                      {product.brand_id ? (
                        <Link to={`/brands/${product.brand_id}`} className="brand-link" onClick={(e) => e.stopPropagation()}>
                          {brands.find(b => b.id === product.brand_id)?.brand_name || product.brand_name}
                        </Link>
                      ) : (
                        <span className="mobile-brand-name">{product.brand_name || '-'}</span>
                      )}
                      {product.sku && (
                        <code className="mobile-sku">{product.sku}</code>
                      )}
                    </div>
                    <div className="mobile-info-title">
                      {product.title || '-'}
                    </div>
                    <div className="mobile-info-row3">
                      <span className="mobile-material-label">画像</span>
                      <span className="image-count-value">{product.image_count ?? 0}</span>
                      <span className="mobile-material-label">共有</span>
                      {renderMaterialPill('shared', product.cloud_material_count ?? 0, () => openWithProductFilter(`manual:${product.id}`, 'shared'))}
                      <span className="mobile-material-label">個人</span>
                      {renderMaterialPill('personal', product.temp_material_count ?? 0, () => openWithProductFilter(`manual:${product.id}`, 'personal'))}
                    </div>
                    <div className="mobile-info-row4">
                      <span className="mobile-date">{formatDate(product.updated_at || product.created_at)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
